import { type FC } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { EmptyState, HeaderSection, ListCard } from '@/components/fragment';
import { Button, Input } from '@zawarski/eti-ui';
import { Badge } from '@/components/ui/badge';
import { PageMaxLayout } from '@/components/layout';
import { useRootDimensions } from '@/hooks/useRootDimensions';
import { useIsMobile } from '@/hooks/use-mobile';
import type { TeamsProps } from './Teams.props';

const TeamsView: FC<TeamsProps> = ({
  count,
  searchTerm,
  teams,
  isLoading,
  isLoadingMore,
  hasMore,
  handleAddClick,
  handleRowClick,
  handleSearchChange,
  sentinelRef,
}) => {
  const dimensions = useRootDimensions();
  const isMobile = useIsMobile();

  return (
    <div className='h-full w-full'>
      {/* Header Section */}
      <HeaderSection
        title='Teams'
        customBadge={
          <Badge variant='secondary' className='bg-card text-card-foreground'>
            {count} entries
          </Badge>
        }
        tableSearchNode={
          <div className='flex items-center gap-4'>
            <Input
              value={searchTerm}
              onChange={(ev) => handleSearchChange(ev.target.value)}
              startAdornment={<Search className='text-foreground size-4 opacity-60' />}
              placeholder='Search teams...'
              className='text-foreground max-w-md flex-1'
            />
            <Button
              variant={'outline'}
              onClick={handleAddClick}
              className='gap-2'
              endIcon={<Plus className='h-4 w-4' />}>
              Add new
            </Button>
          </div>
        }
      />

      {/* Teams List */}
      <PageMaxLayout dimensions={dimensions} isMobile={isMobile}>
        <div className='w-full space-y-3'>
          {isLoading ? (
            <div className='flex flex-col items-center justify-center py-12'>
              <Loader2 className='text-primary size-8 animate-spin' />
              <p className='text-muted-foreground mt-4'>Loading teams...</p>
            </div>
          ) : teams.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {teams.map((team) => (
                <ListCard
                  key={team.id}
                  title={<p className='text-card-foreground font-semibold'>{team.team_name}</p>}
                  subTitle={
                    <p className='text-muted-foreground/70 text-sm'>
                      Areas assigned: {team.parcels?.length ?? 0}
                    </p>
                  }
                  onClick={() => handleRowClick(team.id)}
                />
              ))}
              {/* Sentinel for infinite scroll */}
              {hasMore && <div ref={sentinelRef} className='h-1' />}
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className='flex justify-center py-4'>
                  <Loader2 className='text-primary size-6 animate-spin' />
                </div>
              )}
            </>
          )}
        </div>
      </PageMaxLayout>
    </div>
  );
};

export default TeamsView;
